import {
  ApplicationService,
  ApplicationStatus,
  ApplicationWizardStep,
} from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import { useError } from '@/hooks/useError'
import { PropsWithChildren, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router'

interface GuardedStep {
  /** The fully resolved path of the step's page. */
  path: string
  /** The step as the server names it. */
  key: ApplicationWizardStep
}

interface ApplicationStepGuardProps {
  applicationId: string
  /** Only an application the applicant can still edit is guarded. */
  status: ApplicationStatus | undefined
  /** The flow's steps, in the order the applicant meets them. */
  steps: readonly GuardedStep[]
  /** The page after the last step. It is only reachable once every step is complete. */
  afterLastStepPath: string
}

interface GuardResult {
  pathname: string
  redirectTo: string | null
}

const INCOMPLETE_STEP_MESSAGE =
  'Please complete the earlier steps of the application before continuing.'

// CRD-018: mirrors ApplicationStatuses.ApplicantEditable on the server. Any other application is
// routed by its status: an application under review is sent to the submitted page, and turning it
// back to a step would send it round in a loop.
const GUARDED_STATUSES: readonly ApplicationStatus[] = [
  ApplicationStatus.IN_PROGRESS,
  ApplicationStatus.REJECTED,
]

/**
 * Keeps an applicant from opening a step before the ones ahead of it are complete, whether by
 * typing the URL or from browser history. The server decides what is complete and checks again
 * on submit. The state is read fresh on every navigation, because the step just saved is what
 * makes the next one reachable.
 *
 * Only the first page waits for the check. After that, a step shows at once while it is checked
 * in the background, so moving between steps does not pause on the lookup, and a step that is not
 * reachable yet is turned back once the check returns.
 */
export function ApplicationStepGuard({
  applicationId,
  status,
  steps,
  afterLastStepPath,
  children,
}: PropsWithChildren<ApplicationStepGuardProps>) {
  const { pathname } = useLocation()
  const [result, setResult] = useState<GuardResult>()
  const isGuarded = status !== undefined && GUARDED_STATUSES.includes(status)

  // APP-005: a step opens only when the steps before it are complete
  useEffect(() => {
    if (!isGuarded) return

    const targetIndex =
      pathname === afterLastStepPath
        ? steps.length
        : steps.findIndex((step) => step.path === pathname)

    // Not a wizard page, so there is nothing to guard.
    if (targetIndex === -1) {
      setResult({ pathname, redirectTo: null })
      return
    }

    let isCancelled = false
    ApplicationService.getApplicationCompleteness(applicationId)
      .then((completeness) => {
        if (isCancelled) return
        // Every step up to and including the first incomplete one is reachable.
        const furthestAllowed = steps.findIndex(
          (step) => step.key === completeness.firstIncompleteStep
        )
        const isPastIt = furthestAllowed !== -1 && targetIndex > furthestAllowed
        setResult({
          pathname,
          redirectTo: isPastIt ? steps[furthestAllowed].path : null,
        })
      })
      .catch((e: unknown) => {
        if (isCancelled) return
        // The server refuses an incomplete submit, so failing open here costs the applicant a
        // later error instead of locking them out of the wizard over a failed lookup.
        console.error('There was an error checking the application: ', e)
        setResult({ pathname, redirectTo: null })
      })

    return () => {
      isCancelled = true
    }
  }, [afterLastStepPath, applicationId, isGuarded, pathname, steps])

  if (!isGuarded) return <>{children}</>

  // Nothing has been checked yet, so wait rather than flash a step that may be turned back.
  if (result === undefined) return <Loading variant="fullscreen" />

  if (result.pathname === pathname && result.redirectTo !== null)
    return <TurnBack path={result.redirectTo} />

  return <>{children}</>
}

function TurnBack({ path }: { path: string }) {
  const { showError } = useError()

  useEffect(() => {
    showError(INCOMPLETE_STEP_MESSAGE)
  }, [showError])

  return <Navigate to={path} replace state={{ direction: 'backward' }} />
}
